/**
 * SRT Subtitle Parser Utility
 * Parses .srt subtitle files and provides time-based lookup
 */

export interface SubtitleCue {
  index: number;
  startTime: number; // in seconds
  endTime: number;   // in seconds
  text: string;
}

/**
 * Parse SRT timestamp to seconds
 * Format: HH:MM:SS,mmm
 * Example: 00:00:03,000 -> 3.0
 */
function parseTimestamp(timestamp: string): number {
  const match = timestamp.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
  if (!match) return 0;

  const [, hours, minutes, seconds, milliseconds] = match;
  return (
    parseInt(hours, 10) * 3600 +
    parseInt(minutes, 10) * 60 +
    parseInt(seconds, 10) +
    parseInt(milliseconds, 10) / 1000
  );
}

/**
 * Parse SRT file content into subtitle cues
 */
export function parseSRT(srtContent: string): SubtitleCue[] {
  const cues: SubtitleCue[] = [];

  // Split by double newlines (separates subtitle blocks)
  const blocks = srtContent.trim().split(/\n\s*\n/);

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length < 3) continue; // Invalid block

    const index = parseInt(lines[0], 10);
    if (isNaN(index)) continue;

    // Parse time range (line 2)
    const timeMatch = lines[1].match(/(\S+)\s*-->\s*(\S+)/);
    if (!timeMatch) continue;

    const startTime = parseTimestamp(timeMatch[1]);
    const endTime = parseTimestamp(timeMatch[2]);

    // Join remaining lines as text (handles multi-line subtitles)
    const text = lines.slice(2).join('\n').trim();

    cues.push({ index, startTime, endTime, text });
  }

  return cues;
}

/**
 * Get active subtitle at a given time
 */
export function getActiveSubtitle(cues: SubtitleCue[], currentTime: number): SubtitleCue | null {
  return cues.find(cue => currentTime >= cue.startTime && currentTime < cue.endTime) || null;
}

/**
 * Fetch and parse SRT file from URL.
 * Missing subtitles (404) silently fall back to no cues so the audio player
 * still works; other failures (network, 5xx) are surfaced as a warning.
 */
export async function fetchAndParseSRT(url: string): Promise<SubtitleCue[]> {
  try {
    const response = await fetch(url);
    if (response.status === 404) return [];
    if (!response.ok) {
      console.warn(`SRT fetch failed (${response.status}): ${url}`);
      return [];
    }
    const srtContent = await response.text();
    return parseSRT(srtContent);
  } catch (error) {
    console.warn(`SRT fetch error for ${url}:`, error);
    return [];
  }
}
